import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useEffect, useState, useRef } from 'react';
import { useForm } from 'react-hook-form';
import classNames from 'classnames';
import {
  useLazyCheckPhoneExistsQuery,
  useLoginMutation,
  useVerifyTwoFAMutation,
  useSendTwoFAMutation,
} from 'redux/Api/authApi/authApi';
import { Loader } from '@mantine/core';
import { IconEyeOpen, IconEyeClosed } from 'pages/Profile/icons';
import { LoginCompanySelect } from 'components/CompanySelect/LoginCompanySelect';
import PhoneForm from 'components/PhoneForm';
import s from './Login.module.scss';
import logo from './img/logo.svg';

export const Login = () => {
  const [loading, setLoading] = useState(false);
  const [authError, setAuthError] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showLoginForm, setShowLoginForm] = useState(false);
  const [showCompanySelect, setShowCompanySelect] = useState(false);
  const [showCompanySelectReplace, setShowCompanySelectReplace] = useState(false);
  const [showTwoFA, setShowTwoFA] = useState(false);
  const [twoFAToken, setTwoFAToken] = useState('');
  const [twoFAError, setTwoFAError] = useState('');
  const [registrationSuccess, setRegistrationSuccess] = useState(false);
  const [allowPhoneEdit, setAllowPhoneEdit] = useState(false);
  const [countdownSeconds, setCountdownSeconds] = useState(0);
  const [twoFACountdownSeconds, setTwoFACountdownSeconds] = useState(60);
  const navigate = useNavigate();
  const location = useLocation();
  const registrationSuccessState = location.state?.registrationSuccess;
  const phoneFromRegistration = location.state?.phone;
  const restorePhone = location.state?.restorePhone;
  const showPhoneForm = location.state?.showPhoneForm;
  const formRef = useRef(null);
  const phoneInputRef = useRef(null);
  const passwordInputRef = useRef(null);

  const [checkPhoneExists] = useLazyCheckPhoneExistsQuery();
  const [login] = useLoginMutation();
  const [verifyTwoFA] = useVerifyTwoFAMutation();
  const [sendTwoFA] = useSendTwoFAMutation();

  const mainForm = useForm({
    defaultValues: {
      phone: '',
      password: '',
      twofa_code: '',
    },
    mode: 'onChange',
  });

  const passwordField = mainForm.register('password', {
    required: showLoginForm ? 'Пароль обязателен' : false,
  });

  const cleanPhoneForForm = (phone) => {
    if (!phone) return '';
    const digits = phone.replace(/\D/g, '');
    return digits.startsWith('7') ? digits.substring(1) : digits;
  };

  useEffect(() => {
    if (restorePhone && showPhoneForm) {
      const cleanPhone = cleanPhoneForForm(restorePhone);
      mainForm.setValue('phone', cleanPhone);
      setShowLoginForm(false);
      setShowCompanySelect(false);
      setRegistrationSuccess(false);
      setAllowPhoneEdit(false);
      navigate('/login', { replace: true });
    }
  }, [restorePhone, showPhoneForm, mainForm, navigate]);

  useEffect(() => {
    if (registrationSuccessState) {
      setRegistrationSuccess(true);
      setAllowPhoneEdit(true);
      if (phoneFromRegistration) {
        const cleanPhone = cleanPhoneForForm(phoneFromRegistration);
        mainForm.setValue('phone', cleanPhone);
        setShowLoginForm(true);
      }
      navigate('/login', { replace: true });
    }
  }, [registrationSuccessState, phoneFromRegistration, navigate, mainForm]);

  const handlePhoneSubmit = async (data) => {
    const phone = data.phone;
    const formattedPhone = phone.length < 11 ? `7${phone}` : phone;
    setLoading(true);

    try {
      const result = await checkPhoneExists({ phone: formattedPhone }).unwrap();

      if (result.registration) {
        navigate('/registration/step1', {
          state: {
            phone: formattedPhone,
          },
        });
      } else {
        setShowLoginForm(true);
        setAllowPhoneEdit(false);
        mainForm.setValue('phone', phone);
      }
    } catch (error) {
      if (error.status === 422) {
        setAuthError(
          error?.data?.message || 'Пользователь с данным номером телефона не найден',
        );
      } else {
        setAuthError('Произошла ошибка при проверке номера телефона');
      }
    }
    setLoading(false);
  };

  const handleLoginSubmit = async (data) => {
    setAuthError('');
    s;
    setTwoFAError('');
    setCountdownSeconds(0);
    const phone = data.phone.length < 11 ? `7${data.phone}` : data.phone;

    try {
      setLoading(true);
      const result = await login({ password: data.password, phone }).unwrap();

      const twoFaRequired = (result && result['2fa_required']) || false;

      if (result.device_token) {
        localStorage.setItem('device_token', result.device_token);
      }

      if (twoFaRequired) {
        const tokenFromStep = result['2fa_token'];
        if (!tokenFromStep) {
          setAuthError('Не удалось начать 2FA. Повторите попытку позже.');
          return;
        }
        setTwoFAToken(tokenFromStep);
        setShowTwoFA(true);
        setShowLoginForm(false);
        setTwoFACountdownSeconds(60);
        return;
      }

      if (result && result.token) {
        localStorage.setItem('token', result.token);
        if (result.role) localStorage.setItem('role', result.role);
        localStorage.setItem('phone', phone);

        if (result.role === 'worksupport') {
          // eslint-disable-next-line no-undef
          const baseUrl = process.env.REACT_APP_BASE_URL || '';
          const targetPage =
            baseUrl === '/' ? '/beneficiaries' : `${baseUrl}/beneficiaries`;
          window.location.replace(targetPage);
        } else {
          localStorage.setItem('showCompanySelect', true);
          setTimeout(() => {
            setShowCompanySelect(true);
          }, 100);
        }
      } else {
        setAuthError('Неправильный логин или пароль');
      }
    } catch (error) {
      console.log('error', error);

      if (error?.data?.seconds_left) {
        setCountdownSeconds(error.data.seconds_left);
        setAuthError(error?.data?.message || 'Произошла ошибка при входе');
      } else {
        setAuthError(error?.data?.message || 'Произошла ошибка при входе');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleTwoFASubmit = async (data) => {
    setAuthError('');
    setTwoFAError('');
    const phone = data.phone.length < 11 ? `7${data.phone}` : data.phone;
    const codeString = (data.twofa_code || '').toString().replace(/\D/g, '');
    if (!/^\d{6}$/.test(codeString)) {
      setTwoFAError('Введите код из 6 цифр');
      return;
    }
    const code = String(codeString);
    if (!twoFAToken) {
      setAuthError('Отсутствует токен 2FA. Начните вход заново.');
      return;
    }

    try {
      setLoading(true);
      const result = await verifyTwoFA({ phone, code, '2fa_token': twoFAToken }).unwrap();

      if (result && result.token) {
        localStorage.setItem('token', result.token);
        if (result.role) localStorage.setItem('role', result.role);
        localStorage.setItem('phone', phone);
        if (result.device_token) {
          localStorage.setItem('device_token', result.device_token);
        }

        if (result.role === 'worksupport') {
          // eslint-disable-next-line no-undef
          const baseUrl = process.env.REACT_APP_BASE_URL || '';
          const targetPage =
            baseUrl === '/' ? '/beneficiaries' : `${baseUrl}/beneficiaries`;
          window.location.replace(targetPage);
        } else {
          localStorage.setItem('showCompanySelect', true);
          setTimeout(() => {
            setShowCompanySelect(true);
          }, 100);
        }
      } else {
        setTwoFAError('Неверный код 2FA');
      }
    } catch (error) {
      setTwoFAError(error?.data?.message || 'Произошла ошибка при подтверждении 2FA');
    } finally {
      setLoading(false);
    }
  };

  const handleResendTwoFA = async () => {
    const phone = mainForm.getValues('phone');
    const formattedPhone = phone.length < 11 ? `7${phone}` : phone;

    try {
      setLoading(true);
      setTwoFAError('');
      const result = await sendTwoFA({ phone: formattedPhone }).unwrap();

      if (result['2fa_token']) {
        setTwoFAToken(result['2fa_token']);
        setTwoFACountdownSeconds(60);
      }
    } catch (error) {
      setTwoFAError(error?.data?.message || 'Не удалось отправить код повторно');
    } finally {
      setLoading(false);
    }
  };

  const getSubmitHandler = () => {
    if (showTwoFA) return handleTwoFASubmit;
    return !showLoginForm ? handlePhoneSubmit : handleLoginSubmit;
  };

  const handleBackToPhone = () => {
    const currentPhoneValue = mainForm.getValues('phone');

    setShowLoginForm(false);
    setShowCompanySelect(false);
    setShowTwoFA(false);
    setTwoFAToken('');
    setTwoFACountdownSeconds(60);
    mainForm.reset();
    setAuthError('');
    setRegistrationSuccess(false);
    setAllowPhoneEdit(false);
    setShowPassword(false);
    setLoading(false);
    setCountdownSeconds(0);

    localStorage.removeItem('token');
    localStorage.removeItem('phone');
    localStorage.removeItem('showCompanySelect');

    if (currentPhoneValue) {
      mainForm.setValue('phone', currentPhoneValue);
    }
  };

  useEffect(() => {
    if (showLoginForm && !showTwoFA && passwordInputRef.current) {
      passwordInputRef.current.focus();
    }
  }, [showLoginForm, showTwoFA]);

  useEffect(() => {
    const token = localStorage.getItem('token');
    const role = localStorage.getItem('role');
    const showCompanySelect = localStorage.getItem('showCompanySelect');

    if (showCompanySelect) {
      setShowCompanySelectReplace(true);
      return;
    }

    localStorage.removeItem('showCompanySelect');
    if (token && role) {
      navigate(role === 'director' ? '/orders' : '/beneficiaries', {
        replace: true,
      });
    }
  }, [navigate]);

  useEffect(() => {
    let syncTimer = null;

    const syncAuthFromAnotherTab = () => {
      if (syncTimer) clearTimeout(syncTimer);
      syncTimer = setTimeout(() => {
        const token = localStorage.getItem('token');
        const role = localStorage.getItem('role');
        const shouldShowCompanySelect = Boolean(
          localStorage.getItem('showCompanySelect'),
        );

        if (shouldShowCompanySelect) {
          setShowCompanySelectReplace(true);
          setShowCompanySelect(true);
          return;
        }

        if (token && role) {
          const targetPage = role === 'director' ? '/orders' : '/beneficiaries';
          window.location.replace(targetPage);
          return;
        }

        window.location.reload();
      }, 50);
    };

    const handleStorageChange = (e) => {
      if (['token', 'role', 'showCompanySelect'].includes(e.key)) {
        syncAuthFromAnotherTab();
      }
    };

    window.addEventListener('storage', handleStorageChange);
    return () => {
      if (syncTimer) clearTimeout(syncTimer);
      window.removeEventListener('storage', handleStorageChange);
    };
  }, []);

  useEffect(() => {
    let interval = null;
    if (countdownSeconds > 0) {
      interval = setInterval(() => {
        setCountdownSeconds((seconds) => {
          if (seconds <= 1) {
            if (showLoginForm) {
              setAuthError('');
            }
            return 0;
          }
          return seconds - 1;
        });
      }, 1000);
    }
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [countdownSeconds, showLoginForm]);

  useEffect(() => {
    let interval = null;
    if (twoFACountdownSeconds > 0 && showTwoFA) {
      interval = setInterval(() => {
        setTwoFACountdownSeconds((seconds) => {
          if (seconds <= 1) {
            return 0;
          }
          return seconds - 1;
        });
      }, 1000);
    }
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [twoFACountdownSeconds, showTwoFA]);

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token) {
      // eslint-disable-next-line no-undef
      fetch(`${process.env.REACT_APP_API_URL}/api/user`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      })
        .then((res) => {
          if (res.status === 401 || res.status === 403) {
            localStorage.removeItem('token');
            localStorage.removeItem('role');
            localStorage.removeItem('phone');
            localStorage.removeItem('can_manage');
            localStorage.removeItem('showCompanySelect');
            window.location.href = '/login';
          }
        })
        .catch((error) => {
          console.log('error', error);
        });
    }
  }, []);

  if (showCompanySelect || showCompanySelectReplace) {
    return <LoginCompanySelect />;
  }

  return (
    <div className={s.enter}>
      <div className={s.inner}>
        <div className={s.logo}>
          <img src={logo} alt='Скилла' />
        </div>

        <h1 className={s.title}>
          {showTwoFA ? 'Подтверждение входа' : 'Личный кабинет'}
        </h1>

        <form
          ref={formRef}
          className={s.form}
          onSubmit={mainForm.handleSubmit(getSubmitHandler())}
          name='login-form'
          autoComplete='on'>
          <div className={s.inputGroup} style={{ display: showTwoFA ? 'none' : 'block' }}>
            <label htmlFor='phone'>Введи номер телефона</label>
            <PhoneForm
              control={mainForm.control}
              name='phone'
              error={
                (authError && !showLoginForm) || mainForm.formState.errors?.phone?.message
                  ? authError || mainForm.formState.errors?.phone?.message
                  : undefined
              }
              inputClassName={classNames(
                s.input,
                (authError && !showLoginForm) || mainForm.formState.errors?.phone?.message
                  ? s.input_error
                  : '',
                (!allowPhoneEdit && registrationSuccess) ||
                  (!registrationSuccess && showLoginForm) ||
                  showTwoFA
                  ? s.inputDisabled
                  : '',
              )}
              inputRef={phoneInputRef}
              placeholder='+7 (000) 000-00-00'
              readOnly={
                (!allowPhoneEdit && registrationSuccess) ||
                (!registrationSuccess && showLoginForm) ||
                showTwoFA
              }
              onAfterChange={() => {
                if (!showLoginForm) setAuthError('');
              }}
            />
            {authError && !showLoginForm && (
              <div className={s.errorMessage}>{authError}</div>
            )}
            {mainForm.formState.errors?.phone?.message && (
              <div className={s.errorMessage}>
                {mainForm.formState.errors.phone.message}
              </div>
            )}
          </div>

          <div
            className={s.inputGroup}
            style={{ display: showLoginForm ? 'block' : 'none' }}>
            <label htmlFor='password'>Пароль</label>
            <div className={s.passwordWrapper}>
              <input
                type={showPassword ? 'text' : 'password'}
                required={showLoginForm}
                placeholder='******'
                className={classNames(
                  s.input,
                  (authError && showLoginForm) ||
                    mainForm.formState.errors?.password?.message
                    ? s.input_error
                    : '',
                )}
                autoComplete='current-password'
                name='password'
                id='password'
                tabIndex={showLoginForm ? 0 : -1}
                {...passwordField}
                onChange={(e) => {
                  passwordField.onChange(e);
                  if (authError && showLoginForm) {
                    setAuthError('');
                  }
                }}
                ref={(element) => {
                  passwordInputRef.current = element;
                  passwordField.ref(element);
                }}
              />
              <button
                type='button'
                className={s.eyeButton}
                onClick={() => setShowPassword(!showPassword)}
                disabled={loading}
                style={{ display: showLoginForm ? 'block' : 'none' }}>
                {showPassword ? (
                  <IconEyeClosed size={16} color='#ADBFDF' />
                ) : (
                  <IconEyeOpen size={16} color='#ADBFDF' />
                )}
              </button>
            </div>
            {authError && showLoginForm && (
              <div className={s.errorMessage}>{authError}</div>
            )}
            {mainForm.formState.errors?.password?.message && (
              <div className={s.errorMessage}>
                {mainForm.formState.errors.password.message}
              </div>
            )}

            <div
              className={s.forgotPassword}
              style={{ display: showLoginForm ? 'block' : 'none' }}>
              <Link to='/forgot-password'>Забыли пароль?</Link>
            </div>
          </div>

          <div className={s.inputGroup} style={{ display: showTwoFA ? 'block' : 'none' }}>
            <label>Код из СМС</label>
            <input
              type='text'
              placeholder='000000'
              maxLength={6}
              className={classNames(
                s.input,
                (twoFAError || mainForm.formState.errors?.twofa_code?.message) &&
                  s.input_error,
              )}
              {...mainForm.register('twofa_code', {
                required: showTwoFA ? 'Введите код из СМС' : false,
                pattern: showTwoFA
                  ? {
                      value: /^\d{6}$/,
                      message: 'Код должен содержать 6 цифр',
                    }
                  : undefined,
                onChange: (e) => {
                  const value = e.target.value.replace(/\D/g, '');
                  e.target.value = value;
                  if (twoFAError) setTwoFAError('');
                },
              })}
            />
            {mainForm.formState.errors?.twofa_code?.message && (
              <div className={s.errorMessage} style={{ marginTop: '4px' }}>
                {mainForm.formState.errors.twofa_code.message}
              </div>
            )}
            {twoFAError && !mainForm.formState.errors?.twofa_code?.message && (
              <div className={s.errorMessage} style={{ marginTop: '4px' }}>
                {twoFAError}
              </div>
            )}
            {showTwoFA && twoFACountdownSeconds > 0 && (
              <div className={s.countdownMessage} style={{ marginTop: '4px' }}>
                Отправить код повторно можно через {twoFACountdownSeconds} сек
              </div>
            )}
            {showTwoFA && twoFACountdownSeconds === 0 && (
              <button
                type='button'
                className={s.countdownReset}
                onClick={handleResendTwoFA}
                disabled={loading}>
                Отправить СМС повторно
              </button>
            )}
          </div>

          {!showTwoFA && countdownSeconds > 0 && (
            <div className={s.errorMessage}>
              Повторить попытку можно через: {countdownSeconds} сек
            </div>
          )}

          <button
            type='submit'
            disabled={loading || countdownSeconds > 0}
            className={s.submitButton}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              {showTwoFA ? 'Подтвердить' : 'Войти'}{' '}
              {loading && <Loader size='xs' color='white' />}
            </div>
          </button>

          {(showLoginForm || showTwoFA) && (
            <button type='button' className={s.backButton} onClick={handleBackToPhone}>
              Назад
            </button>
          )}

          {showTwoFA && (
            <>
              <input
                type='tel'
                name='username'
                value={phoneInputRef?.current?.value || ''}
                autoComplete='username'
                style={{
                  position: 'absolute',
                  left: '-9999px',
                  opacity: 0,
                  pointerEvents: 'none',
                }}
                tabIndex={-1}
                readOnly
              />
              <input
                type='password'
                name='password'
                value=''
                autoComplete='new-password'
                style={{
                  position: 'absolute',
                  left: '-9999px',
                  opacity: 0,
                  pointerEvents: 'none',
                }}
                tabIndex={-1}
                readOnly
              />
            </>
          )}

          {/* {!showLoginForm && (
            <button
              type='button'
              className={classNames(
                s.registerButton2,
                !isPhoneValid && s.registerButtonDisabled,
              )}
              disabled={!isPhoneValid}
              onClick={handleRegistrationClick}>
              Зарегистрироваться
            </button>
          )} */}
        </form>

        <div className={s.footer}>
          <span>© {new Date().getFullYear()} go.skilla.ru</span>
        </div>
      </div>
    </div>
  );
};
